import { useEffect, useRef } from "react";

/**
 * Refreshes data only while the document is visible.
 *
 * - focus/visibility return triggers an immediate refresh;
 * - interval polling is optional and is suspended while hidden;
 * - overlapping refreshes started by this hook are prevented;
 * - callback receives the trigger reason: interval | focus | visibility;
 * - callback changes do not recreate timers/listeners;
 * - background refresh failures are contained so they do not create
 *   unhandled promise rejections or interrupt the current screen state.
 */
export function useVisibleRefresh(callback, { enabled = true, intervalMs = 0 } = {}) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return undefined;

    let timer = null;
    let inFlight = false;
    let disposed = false;

    const run = async (reason) => {
      if (disposed || inFlight || document.visibilityState !== "visible") return;
      inFlight = true;
      try {
        await callbackRef.current?.(reason);
      } catch {
        // Refreshes started by focus/polling are best-effort. Initial loads and
        // explicit user actions keep their own visible error handling.
      } finally {
        inFlight = false;
      }
    };

    const stopTimer = () => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    const startTimer = () => {
      stopTimer();
      if (intervalMs > 0 && document.visibilityState === "visible") {
        timer = window.setInterval(() => { void run("interval"); }, intervalMs);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void run("visibility");
        startTimer();
      } else {
        stopTimer();
      }
    };

    const handleFocus = () => {
      if (document.visibilityState === "visible") void run("focus");
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleFocus);
    startTimer();

    return () => {
      disposed = true;
      stopTimer();
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleFocus);
    };
  }, [enabled, intervalMs]);
}
